import React, { useEffect, useState } from 'react';
import {
  PieChart, Pie, BarChart, Bar, LineChart, Line,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  ResponsiveContainer, Cell
} from 'recharts';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:4000';
const COLORS = ['#8884d8','#82ca9d','#ffc658','#ff8042','#8dd1e1','#a4de6c','#d0ed57','#a28fd0'];

function fetchReport(endpoint, params = {}) {
  const url = new URL(`${API_BASE_URL}/api/reports/${endpoint}`);
  Object.entries(params).forEach(([k, v]) => v && url.searchParams.append(k, v));
  return fetch(url).then(r => r.json());
}

/** Normalise a category value that might be a JSON string */
function parseCategoryName(raw) {
  if (!raw) return '';
  if (typeof raw === 'string' && raw.startsWith('{')) {
    try { return JSON.parse(raw).name || raw; } catch { return raw; }
  }
  return raw;
}

/** KPI card */
function KPI({ label, value, sub }) {
  return (
    <div className="bg-white dark:bg-gray-800 rounded shadow p-4 flex flex-col">
      <span className="text-xs text-gray-400 uppercase tracking-wide mb-1">{label}</span>
      <span className="text-2xl font-bold dark:text-white">{value}</span>
      {sub && <span className="text-xs text-gray-400 mt-1">{sub}</span>}
    </div>
  );
}

/** Chart card wrapper */
function Card({ title, children, wide }) {
  return (
    <div className={`bg-white dark:bg-gray-800 p-4 rounded shadow ${wide ? 'col-span-1 md:col-span-2 xl:col-span-3' : ''}`}>
      <h3 className="font-semibold mb-3 text-sm dark:text-gray-100">{title}</h3>
      {children}
    </div>
  );
}

export default function ReportCharts({ filters }) {
  const [data, setData]       = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    Promise.all([
      fetchReport('hours-by-category', filters),
      fetchReport('hours-by-user', filters),
      fetchReport('hours-by-date', filters),
      fetchReport('hours-by-weekday', filters),
      fetchReport('hours-by-department', filters),
      fetchReport('hours-by-client', filters),
      fetchReport('category-trend', filters),
    ]).then(([byCategory, byUser, byDate, byWeekday, byDept, byClient, trend]) => {
      setData({ byCategory, byUser, byDate, byWeekday, byDept, byClient, trend });
      setLoading(false);
    }).catch(() => setLoading(false));
  }, [JSON.stringify(filters)]);

  if (loading) return (
    <div className="flex items-center justify-center h-48 text-gray-400">Loading reports…</div>
  );

  // Normalise category names for pie chart
  const categoryData = (data.byCategory || [])
    .map(r => ({
      ...r,
      category: parseCategoryName(r.category),
    }))
    .filter(r => r.category && Number(r.total_hours) > 0);

  // KPI calculations
  const totalHours  = (data.byCategory || []).reduce((s, r) => s + (r.total_hours || 0), 0);
  const totalUsers  = (data.byUser || []).length;
  const totalDays   = (data.byDate || []).length;
  const avgPerDay   = totalDays > 0 ? (totalHours / totalDays).toFixed(1) : '—';

  const axisStyle   = { fontSize: 11, fill: '#6b7280' };
  const tooltipStyle = { contentStyle: { fontSize: 12 } };

  return (
    <div>
      {/* ── KPI row ── */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <KPI label="Total Hours" value={totalHours.toFixed(1)} />
        <KPI label="Active Users" value={totalUsers} />
        <KPI label="Active Days" value={totalDays} />
        <KPI label="Avg Hours / Day" value={avgPerDay} />
      </div>

      {/* ── Charts grid ── */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">

        {/* Pie – by category */}
        <Card title="Hours by Category">
          <ResponsiveContainer width="100%" height={250}>
            <PieChart>
              <Pie data={categoryData} dataKey="total_hours" nameKey="category" cx="50%" cy="50%" outerRadius={80} label={({ category, percent }) => `${category} ${(percent*100).toFixed(0)}%`} labelLine={false}>
                {categoryData.map((_, idx) => <Cell key={idx} fill={COLORS[idx % COLORS.length]} />)}
              </Pie>
              <Tooltip formatter={(v, n) => [v.toFixed(1) + ' h', n]} />
              <Legend formatter={v => v} />
            </PieChart>
          </ResponsiveContainer>
        </Card>

        {/* Bar – by user */}
        <Card title="Hours by User">
          <ResponsiveContainer width="100%" height={250}>
            <BarChart data={data.byUser || []} margin={{ top: 5, right: 10, left: 0, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
              <XAxis dataKey="user" tick={axisStyle} />
              <YAxis tick={axisStyle} />
              <Tooltip {...tooltipStyle} formatter={v => [v.toFixed(1) + ' h', 'Hours']} />
              <Bar dataKey="total_hours" name="Hours" fill="#8884d8" radius={[3,3,0,0]} />
            </BarChart>
          </ResponsiveContainer>
        </Card>

        {/* Bar – by department */}
        <Card title="Hours by Department">
          <ResponsiveContainer width="100%" height={250}>
            <BarChart data={data.byDept || []} margin={{ top: 5, right: 10, left: 0, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
              <XAxis dataKey="department" tick={axisStyle} />
              <YAxis tick={axisStyle} />
              <Tooltip {...tooltipStyle} formatter={v => [v.toFixed(1) + ' h', 'Hours']} />
              <Bar dataKey="total_hours" name="Hours" fill="#ff8042" radius={[3,3,0,0]} />
            </BarChart>
          </ResponsiveContainer>
        </Card>

        {/* Bar – by client */}
        <Card title="Hours by Client">
          <ResponsiveContainer width="100%" height={250}>
            <BarChart data={data.byClient || []} margin={{ top: 5, right: 10, left: 0, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
              <XAxis dataKey="client_name" tick={axisStyle} />
              <YAxis tick={axisStyle} />
              <Tooltip {...tooltipStyle} formatter={v => [v.toFixed(1) + ' h', 'Hours']} />
              <Bar dataKey="total_hours" name="Hours" fill="#a4de6c" radius={[3,3,0,0]} />
            </BarChart>
          </ResponsiveContainer>
        </Card>

        {/* Bar – by weekday */}
        <Card title="Hours by Weekday">
          <ResponsiveContainer width="100%" height={250}>
            <BarChart data={data.byWeekday || []} margin={{ top: 5, right: 10, left: 0, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
              <XAxis dataKey="weekday" tick={axisStyle} />
              <YAxis tick={axisStyle} />
              <Tooltip {...tooltipStyle} formatter={v => [v.toFixed(1) + ' h', 'Hours']} />
              <Bar dataKey="total_hours" name="Hours" fill="#ffc658" radius={[3,3,0,0]} />
            </BarChart>
          </ResponsiveContainer>
        </Card>

        {/* Line – hours by date (full width) */}
        <Card title="Hours by Date" wide>
          <ResponsiveContainer width="100%" height={280}>
            <LineChart data={data.byDate || []} margin={{ top: 5, right: 20, left: 0, bottom: 20 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
              <XAxis dataKey="date" tick={{ ...axisStyle, angle: -35, textAnchor: 'end' }} height={55} />
              <YAxis tick={axisStyle} />
              <Tooltip {...tooltipStyle} formatter={v => [v.toFixed(1) + ' h', 'Hours']} />
              <Legend formatter={() => 'Hours'} />
              <Line type="monotone" dataKey="total_hours" name="Hours" stroke="#82ca9d" strokeWidth={2} dot={false} />
            </LineChart>
          </ResponsiveContainer>
        </Card>

        {/* Line – category trend (full width) */}
        <Card title="Category Usage Over Time" wide>
          <ResponsiveContainer width="100%" height={280}>
            <LineChart data={data.trend || []} margin={{ top: 5, right: 20, left: 0, bottom: 20 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
              <XAxis dataKey="date" tick={{ ...axisStyle, angle: -35, textAnchor: 'end' }} height={55} />
              <YAxis tick={axisStyle} />
              <Tooltip {...tooltipStyle} formatter={v => [v.toFixed(1) + ' h', 'Hours']} />
              <Legend formatter={() => 'Hours'} />
              <Line type="monotone" dataKey="total_hours" name="Hours" stroke="#8884d8" strokeWidth={2} dot={false} />
            </LineChart>
          </ResponsiveContainer>
        </Card>

      </div>
    </div>
  );
}
